import type {ChangeSpec} from '@codemirror/state'
import type {Command, EditorView} from '@codemirror/view'
import type {List, ListItem} from 'mdast'

import {
  buildNextMarkerSyntax,
  buildResetMarkerSyntax,
  findPrefixDelimiter,
} from '../../../language-tools/markdown/list-utils'
import {MarkdownLine} from '../../../language-tools/markdown/markdown-line'
import {isPositionedNode} from '../../../language-tools/syntax-tree/positioned-node'
import {getMarkdownTree} from '../../extensions/markdown-parser'
import {getListSelection, type ListItemsSelection, remarkFollowingItems} from './utils'

/** Get the previous sibling list item if there is one.. */
function getPreviousSibling(list: List, item: ListItem) {
  const children = list.children.filter(n => n.type === 'listItem')
  return children[children.indexOf(item) - 1]
}

/** From the selection, travel up the tree to find a valid item for indenting. */
function findValidItemsToIndent(
  selection: ListItemsSelection,
): {list: List; items: ListItem[]; previousSibling: ListItem} | null {
  let list = selection.list
  let items: ListItem[] = selection.selectedItems
  let previousSibling = items[0] && getPreviousSibling(list, items[0])

  const parents = [...selection.parents]
  while (!previousSibling) {
    const parent = parents.pop()
    if (!parent) return null
    list = parent.list
    items = [parent.item]
    previousSibling = getPreviousSibling(list, parent.item)
  }

  return {list, items, previousSibling}
}

/**
 * Indent any selected list items, automatically adjusting numbering or list markers to match their destination.
 */
export const indentSelectedListItems: Command = (view: EditorView) => {
  if (view.state.selection.ranges.length !== 1) return false

  const tree = getMarkdownTree(view.state)
  const source = view.state.doc.toString()

  const selection = getListSelection(view.state)
  if (!selection) return false

  const itemsToIndent = findValidItemsToIndent(selection)
  if (!itemsToIndent || !itemsToIndent.items[0]) return false

  // lol, I thought we could just indent things by two spaces, but of course it's not that simple. The spec states
  // we must indent by the length of the new parent (current previous sibling) marker: https://github.github.com/gfm/#example-274
  const prevSiblingPrefix = findPrefixDelimiter(itemsToIndent.previousSibling)?.raw ?? '* '
  const indent = ' '.repeat(prevSiblingPrefix.length)

  // The current previous sibling will become the new parent, so its last child will become the new previous sibling
  // from which we need to start calculating the updated markers
  const newPreviousSibling = itemsToIndent.previousSibling.children
    ?.findLast(n => n.type === 'list')
    ?.children?.findLast(n => n.type === 'listItem')

  let nextPrefix = newPreviousSibling
    ? buildNextMarkerSyntax(findPrefixDelimiter(newPreviousSibling)?.raw ?? '* ')
    : buildResetMarkerSyntax(findPrefixDelimiter(itemsToIndent.items[0])?.raw ?? '* ')

  // Now we're ready to calculate the changes: indent each line by indentSize, and update the prefixes of each item
  const changes: ChangeSpec[] = itemsToIndent.items.map(item => {
    if (!isPositionedNode(item)) return []

    // It's trickier than you might think to figure out where to insert the spaces on list item continuation lines.
    // The list may be in a blockquote, so we can't just insert them at the beginning of the line. And there could be
    // a blockquote inside the list, so we also can't just insert them at the line's `contentStart`. So, we keep track
    // of where we found the listItemPrefix and we insert them at that point on every line, creating a 'column'.
    let listItemPrefixIndex = -1
    return MarkdownLine.linesAtRange(tree, source, item.position.start.offset, item.position.end.offset - 1).map(
      (line, index) => {
        // On the first line of an item, replace the prefix with the recalculated prefix (and also insert the indent)
        if (index === 0) {
          listItemPrefixIndex = line.prefixes.findIndex(p => p.tokenType === 'listItemPrefix')
          const change = {
            from: line.prefixes[listItemPrefixIndex]?.position?.start?.offset ?? line.start,
            to: line.prefixes[listItemPrefixIndex]?.position?.end?.offset ?? line.contentStart,
            insert: `${indent}${nextPrefix}`,
          }
          nextPrefix = buildNextMarkerSyntax(nextPrefix)
          return change
        }

        // On subsequent lines, just insert the indent at the correct position
        return {
          from: line.prefixes[listItemPrefixIndex]?.position?.start?.offset ?? line.start,
          insert: indent,
        }
      },
    )
  })

  // Phew, we're done! Except...we just indented some items out of a list, so now every item after those items is going
  // to have invalid numbering. So we also need to recalculate the prefixes of all the following items, relative to
  // the current previous sibling of the selection which will now be the prefix sibling of these items:
  const lastItemIndex = itemsToIndent.list.children.indexOf(itemsToIndent.items.at(-1)!)
  changes.push(remarkFollowingItems(itemsToIndent.list, lastItemIndex, prevSiblingPrefix))

  // Okay, *now* we're done.
  view.dispatch({
    scrollIntoView: true,
    userEvent: 'input',
    changes,
  })
  return true
}
